<script lang="ts">
  import { link } from 'svelte-spa-router'
  import { tStore } from '../../../i18n'
  import StatusBadge from '../../../components/StatusBadge.svelte'
  import Tooltip from '../../../components/Tooltip.svelte'
  import { aiScoreColor, countryAlpha2, formatDate, formatValue } from '../../../composables/format'
  import type {
    ProcurementSourceRef,
    ProcurementSortField,
    ProcurementSummary,
    ProcurementType,
    SortOrder,
  } from '../../../api/procurements'

  let {
    procurements,
    loading,
    errorMsg,
    sortField,
    sortDir,
    aiPending,
    bulkMode,
    selectedIds,
    onSort,
    onRunAi,
    onDelete,
    onCancel,
    onToggleSelect,
    onToggleSelectAll,
  }: {
    procurements: ProcurementSummary[]
    loading: boolean
    errorMsg: string | null
    sortField: ProcurementSortField | undefined
    sortDir: SortOrder
    aiPending: Set<number>
    bulkMode: boolean
    selectedIds: Set<number>
    onSort: (field: ProcurementSortField) => void
    onRunAi: (id: number) => void
    onDelete: (p: ProcurementSummary) => void
    onCancel: (id: number) => void
    onToggleSelect: (id: number) => boolean
    onToggleSelectAll: () => boolean
  } = $props()

  // True only when every *selectable* row on this page is selected — rows
  // currently running an analysis can't be selected (their checkbox is
  // disabled), so they must not count against "all selected" any more than
  // they count toward it. Selection persists across paging, so this must not
  // look at selectedIds as a whole (a prior page's selection shouldn't make
  // this page's checkbox appear checked).
  let selectableProcurements = $derived(procurements.filter((p) => !aiPending.has(p.id)))
  let headerCheckboxChecked = $derived(
    selectableProcurements.length > 0 && selectableProcurements.every((p) => selectedIds.has(p.id)),
  )

  function sortIndicator(field: ProcurementSortField): string {
    if (sortField !== field) return '↕'
    return sortDir === 'asc' ? '▲' : '▼'
  }

  function ariaSort(field: ProcurementSortField): 'none' | 'ascending' | 'descending' {
    if (sortField !== field) return 'none'
    return sortDir === 'asc' ? 'ascending' : 'descending'
  }

  function sortLabel(field: ProcurementSortField, column: string): string {
    const state =
      sortField !== field
        ? $tStore('views.procurements.sort.none')
        : sortDir === 'asc'
          ? $tStore('views.procurements.sort.ascending')
          : $tStore('views.procurements.sort.descending')
    return `${column} — ${state}`
  }

  function sourceRefs(p: ProcurementSummary): ProcurementSourceRef[] {
    return p.sourceRefs?.length
      ? p.sourceRefs
      : [
          {
            sourceId: p.sourceId,
            sourceName: p.sourceName,
            externalId: p.externalId,
            tedId: null,
            url: p.url,
            errorKind: null,
          },
        ]
  }

  function sourceLabel(name: string): string {
    return name.trim().toUpperCase()
  }

  function typeTone(t: ProcurementType | null) {
    if (t === 'public') return 'info' as const
    return 'neutral' as const
  }

  // "Re-run AI" once an analysis has ever been attempted — even if it failed
  // or found no documents and so left aiScore null forever. aiScore alone
  // can't tell "never analyzed" apart from "analyzed, but nothing to show".
  function hasBeenAnalyzed(p: ProcurementSummary): boolean {
    return p.aiScore !== null || p.aiAnalysisAttempted
  }
</script>

{#if loading && procurements.length === 0}
  <p class="muted">{$tStore('views.procurements.loading')}</p>
{:else if errorMsg}
  <p class="error">{errorMsg}</p>
{:else if procurements.length === 0}
  <p class="muted">{$tStore('views.procurements.empty')}</p>
{:else}
  <!--
    Keep the table mounted across re-fetches (sort/paging): we only show the
    full "Loading…" placeholder on the very first load, when there are no
    rows yet. On refresh the existing rows stay in place — just dimmed via
    `aria-busy` — so the card height never collapses and the page can't
    jump under the cursor.
  -->
  <div class="table-wrap" class:table-wrap--busy={loading} aria-busy={loading}>
    <table class="table">
      <thead>
        <tr>
          {#if bulkMode}
            <th class="select-col">
              <input
                type="checkbox"
                checked={headerCheckboxChecked}
                onchange={(e) => {
                  const target = e.currentTarget
                  target.checked = onToggleSelectAll()
                }}
                aria-label={$tStore('views.dashboard.selectAllOnPage')}
              />
            </th>
          {/if}
          <th>{$tStore('views.procurements.columns.title')}</th>
          <th>{$tStore('views.procurements.columns.buyer')}</th>
          <th aria-sort={ariaSort('value')}>
            <button
              type="button"
              class="sort-btn"
              class:sort-btn--active={sortField === 'value'}
              onclick={() => onSort('value')}
              aria-label={sortLabel('value', $tStore('views.procurements.columns.value'))}
            >
              {$tStore('views.procurements.columns.value')}
              <span class="sort-ind" aria-hidden="true">{sortIndicator('value')}</span>
            </button>
          </th>
          <th aria-sort={ariaSort('type')}>
            <button
              type="button"
              class="sort-btn"
              class:sort-btn--active={sortField === 'type'}
              onclick={() => onSort('type')}
              aria-label={sortLabel('type', $tStore('views.procurements.columns.type'))}
            >
              {$tStore('views.procurements.columns.type')}
              <span class="sort-ind" aria-hidden="true">{sortIndicator('type')}</span>
            </button>
          </th>
          <th class="score-col" aria-sort={ariaSort('aiScore')}>
            <button
              type="button"
              class="sort-btn"
              class:sort-btn--active={sortField === 'aiScore'}
              onclick={() => onSort('aiScore')}
              aria-label={sortLabel('aiScore', $tStore('views.procurements.columns.aiScore'))}
            >
              {$tStore('views.procurements.columns.aiScore')}
              <span class="sort-ind" aria-hidden="true">{sortIndicator('aiScore')}</span>
            </button>
          </th>
          <th class="date-cell" aria-sort={ariaSort('published')}>
            <button
              type="button"
              class="sort-btn"
              class:sort-btn--active={sortField === 'published'}
              onclick={() => onSort('published')}
              aria-label={sortLabel('published', $tStore('views.procurements.columns.publishedAt'))}
            >
              {$tStore('views.procurements.columns.publishedAt')}
              <span class="sort-ind" aria-hidden="true">{sortIndicator('published')}</span>
            </button>
          </th>
          <th class="date-cell" aria-sort={ariaSort('deadline')}>
            <button
              type="button"
              class="sort-btn"
              class:sort-btn--active={sortField === 'deadline'}
              onclick={() => onSort('deadline')}
              aria-label={sortLabel('deadline', $tStore('views.procurements.columns.deadlineAt'))}
            >
              {$tStore('views.procurements.columns.deadlineAt')}
              <span class="sort-ind" aria-hidden="true">{sortIndicator('deadline')}</span>
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        {#each procurements as p (p.id)}
          <tr
            class:row--running={aiPending.has(p.id)}
            aria-selected={bulkMode ? selectedIds.has(p.id) : undefined}
          >
            {#if bulkMode}
              <td class="select-col">
                <input
                  type="checkbox"
                  checked={selectedIds.has(p.id)}
                  disabled={aiPending.has(p.id)}
                  onchange={(e) => {
                    const target = e.currentTarget
                    target.checked = onToggleSelect(p.id)
                  }}
                  aria-label={p.title}
                />
              </td>
            {/if}
            <td>
              <Tooltip text={p.titleEng}>
                <a href={`/procurements/${p.id}`} use:link class="title-link">
                  {p.title}
                </a>
              </Tooltip>
              <div class="row-meta">
                {#if p.country}
                  <span class="country-badge" title="Country">{countryAlpha2(p.country)}</span>
                {/if}
                {#each sourceRefs(p) as ref (`${ref.sourceId}-${ref.externalId}`)}
                  <span class="source-badge" title={`${ref.sourceName}: ${ref.externalId}`}>
                    {sourceLabel(ref.sourceName)}
                  </span>
                {/each}
                <button
                  type="button"
                  class="btn btn--primary btn--sm run-ai"
                  onclick={() => onRunAi(p.id)}
                  disabled={aiPending.has(p.id)}
                  title={hasBeenAnalyzed(p)
                    ? $tStore('views.dashboard.reRunAiTooltip')
                    : $tStore('views.dashboard.runAiTooltip')}
                  aria-label={hasBeenAnalyzed(p)
                    ? $tStore('views.dashboard.reRunAiTooltip')
                    : $tStore('views.dashboard.runAiTooltip')}
                >
                  {aiPending.has(p.id)
                    ? $tStore('views.dashboard.runAiRunning')
                    : hasBeenAnalyzed(p)
                      ? $tStore('views.dashboard.reRunAi')
                      : $tStore('views.dashboard.runAi')}
                </button>
                {#if aiPending.has(p.id)}
                  <button
                    type="button"
                    class="btn btn--secondary btn--sm cancel-btn"
                    onclick={() => onCancel(p.id)}
                    title={$tStore('views.dashboard.cancelAiTooltip')}
                    aria-label={$tStore('views.dashboard.cancelAiTooltip')}
                  >
                    {$tStore('views.dashboard.cancelAi')}
                  </button>
                {:else}
                  <button
                    type="button"
                    class="btn btn--danger btn--sm delete-btn"
                    onclick={() => onDelete(p)}
                    title={$tStore('views.dashboard.deleteProcurementTooltip')}
                    aria-label={$tStore('views.dashboard.deleteProcurementTooltip')}
                  >
                    {$tStore('views.dashboard.deleteProcurement')}
                  </button>
                {/if}
              </div>
            </td>
            <td>{p.buyerName ?? '—'}</td>
            <td>{formatValue(p.valueEur, p.valueOriginal, p.currency)}</td>
            <td>
              {#if p.procurementType}
                <StatusBadge
                  label={$tStore(`views.procurements.type.${p.procurementType}`)}
                  tone={typeTone(p.procurementType)}
                />
              {:else}
                —
              {/if}
            </td>
            <td class="score-cell">
              {#if p.aiScore !== null}
                <a
                  href={`/procurements/${p.id}?focus=ai-score`}
                  use:link
                  class="score-link"
                  style:color={aiScoreColor(p.aiScore)}
                  title={$tStore('views.dashboard.viewAiScore')}
                >
                  {p.aiScore}%
                </a>
              {:else}
                —
              {/if}
            </td>
            <td class="date-cell">{formatDate(p.publishedAt)}</td>
            <td class="date-cell">{formatDate(p.deadlineAt)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style lang="scss">
  @use '../../../assets/styles/variables' as v;

  .table-wrap {
    overflow-x: auto;
    // Reserve space during a refresh so the dim doesn't also shift layout.
    transition: opacity 0.15s ease;
  }

  // Refresh-in-progress: keep the current rows visible but visibly inert. No
  // pointer-events block — re-fetches abort the previous request, so spamming a
  // sort header stays safe and snappy.
  .table-wrap--busy {
    opacity: 0.55;
  }

  // Header only centres over the column — colour/weight/case come from the
  // global `thead th` rule so the AI score heading matches every other column.
  .score-col {
    text-align: center;
  }

  .score-cell {
    font-weight: 600;
    color: v.$color-text;
    text-align: center;
    font-variant-numeric: tabular-nums;
    // Colour is applied inline via aiScoreColor() (shared with the detail view)
    // so the same percentage always renders in the same band colour.
  }

  // The score doubles as a deep link into the detail page's AI score section.
  // It keeps the band colour (applied inline) and only reveals its link-ness on
  // hover, so the column still reads as a plain figure at rest.
  .score-link {
    font-weight: inherit;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .date-cell {
    white-space: nowrap;
  }

  .select-col {
    width: 1%;
    white-space: nowrap;
    text-align: center;
  }

  // Row currently running an analysis: light green from the palette plus a
  // green left accent, so it reads as "active" (not disabled) and stays
  // distinguishable from the stronger hover / selected greens. Painted on
  // `td` because the global table rules paint cells, which would hide a
  // background set on the `tr`. Text colours are left untouched.
  .row--running td {
    background: v.$color-success-bg-subtle;
  }

  .row--running td:first-child {
    box-shadow: inset 3px 0 0 v.$color-success-fg;
  }

  // Hover and selected win over running (selected declared last); the left
  // accent stays in every state.
  .row--running:hover td {
    @include v.interactive-hover($recolor-text: false);
  }

  .row--running[aria-selected='true'] td,
  .row--running[aria-selected='true']:hover td {
    @include v.interactive-selected($recolor-text: false);
  }

  // Sortable column header. Inherits the global `thead th` typography (colour,
  // weight, uppercase) so a sortable heading is indistinguishable from a plain
  // one until hovered — only the sort glyph hints at interactivity.
  .sort-btn {
    display: inline-flex;
    align-items: center;
    gap: v.$space-xs;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    font: inherit;
    color: inherit;
    text-transform: inherit;
    letter-spacing: inherit;

    &:hover {
      color: v.$color-primary;
    }

    &--active {
      color: v.$color-primary;
    }
  }

  .sort-ind {
    font-size: 0.7rem;
    line-height: 1;
    opacity: 0.7;
  }

  .sort-btn--active .sort-ind {
    opacity: 1;
  }

  .title-link {
    font-weight: 600;
    color: v.$color-text;

    &:hover {
      color: v.$color-primary;
      text-decoration: none;
    }
  }

  .row-meta {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    margin-top: v.$space-xs;
    font-size: 0.8125rem;
    color: v.$color-text-muted;
    flex-wrap: wrap;
  }

  // Compact primary action sized to sit inline with the source badges.
  .run-ai {
    font-size: 0.75rem;
    padding: 0.15rem v.$space-sm;
  }

  .country-badge {
    padding: 0 v.$space-xs;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-sm;
    background: v.$color-surface;
    color: v.$color-text-muted;
    font-family: v.$font-mono;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.45;
  }

  .source-badge {
    padding: 0 v.$space-xs;
    border: 1px solid #bfdbfe;
    border-radius: v.$radius-sm;
    background: #eff6ff;
    color: #1d4ed8;
    font-family: v.$font-mono;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.45;
  }

  .delete-btn {
    font-size: 0.75rem;
    padding: 0.15rem v.$space-sm;
    background: v.$color-danger-fg;
    color: #fff;
    border-color: v.$color-danger-fg;

    &:hover:not(:disabled) {
      background: #991b1b;
      border-color: #991b1b;
    }
  }
</style>
